import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { environment } from '../../../environments/environment';
import { BrowserStorageService } from '../../services/browser-storage.service';
import { EncryptionService } from '../../services/encryption.service';

const RESERVED_SUBDOMAINS = ['www', 'app', 'admin', 'api', 'staging', 'stage', 'sandbox', 'pre', 'dev'];

/**
 * Override de QA: `?tenant=<slug>` en la URL (p. ej. `/login?tenant=imony`) fuerza
 * el tenant PRE-LOGIN sin necesitar DNS ni certificado por marca. Se persiste por
 * PESTAÑA (sessionStorage) para sobrevivir la navegación pre-login
 * (login → verify-code → …) y los reloads. El login limpia sessionStorage, y a
 * partir de ahí manda el `tenant_id` del usuario (el tenant es del usuario).
 * Solo actúa con `environment.tenantThemeEnabled`.
 *
 * NOTA: src/index.html (anti-FOUC) replica esta regla; mantener en sync.
 */
export const TENANT_QA_OVERRIDE_KEY = 'plp_tenant_qa_override';
const TENANT_SLUG_RE = /^[a-z0-9-]{1,100}$/;

@Injectable({ providedIn: 'root' })
export class TenantIdService {
  private resolvedTenantId: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document,
    private browserStorage: BrowserStorageService,
    private encryption: EncryptionService
  ) {}

  /**
   * Tenant resuelto por lo AUTORITATIVO, sin adivinar por DNS:
   * 1. `tenant_id` del usuario en sesión (lo entrega el login) — post-login/reload
   * 2. Override de QA `?tenant=<slug>` (o el persistido en la pestaña) — pre-login
   * 3. `environment.tenantId` (dev/local)
   *
   * `null` = nadie declaró la marca. Ese caso lo resuelve el BACK por el
   * hostname registrado (`/tenants/theme/by-host/`), y por eso está separado de
   * la convención del subdominio: mientras las dos vivían dentro de `resolve()`,
   * un host de tres etiquetas SIEMPRE devolvía candidato y el camino por host
   * nunca se ejercitaba (bug en vivo: `imony-test.tpx1.polipay.io` pedía el
   * theme de `imony-test`, un id que el back no conoce, y recibía `polipay`).
   */
  resolveExplicit(): string | null {
    if (this.resolvedTenantId) return this.resolvedTenantId;

    const fromSession = this.sessionTenantId();
    if (fromSession) {
      this.resolvedTenantId = fromSession;
      return this.resolvedTenantId;
    }

    const fromQa = this.qaOverrideTenantId();
    if (fromQa) {
      this.resolvedTenantId = fromQa;
      return this.resolvedTenantId;
    }

    if (environment.tenantId) {
      this.resolvedTenantId = environment.tenantId;
      return this.resolvedTenantId;
    }

    return null;
  }

  /**
   * La primera etiqueta del host COMO slug. Es una CONVENCIÓN de respaldo, no
   * una resolución: solo acierta si el subdominio se llama igual que la marca,
   * no soporta el dominio propio del cliente (`app.cliente.com` daría `app`) y
   * ata el slug al DNS. No se memoiza a propósito — es un candidato a probar,
   * no un tenant resuelto.
   */
  subdomainCandidate(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    const parts = this.document.location.hostname.split('.');
    if (parts.length < 3) return null;

    const candidate = parts[0];
    return RESERVED_SUBDOMAINS.includes(candidate) ? null : candidate;
  }

  /**
   * Lo autoritativo y, si no hay nada, el candidato del subdominio.
   * Precedencia: sesión > override de QA > `environment.tenantId` > subdominio.
   *
   * ⚠️ Devuelve el candidato del subdominio SIN preguntarle al back si ese slug
   * existe, así que no sirve para elegir el endpoint del tema: `TenantThemeService`
   * usa `resolveExplicit()` + `subdomainCandidate()` por separado. Se conserva
   * para quien solo necesita "la mejor conjetura" del tenant actual
   * (`getTenantId()`).
   */
  resolve(): string | null {
    return this.resolveExplicit() ?? this.subdomainCandidate();
  }

  getTenantId(): string | null {
    return this.resolvedTenantId ?? this.resolve();
  }

  /** Fija el tenant (p. ej. el `tenant_id` del usuario tras el login). */
  override(tenantId: string | null): void {
    this.resolvedTenantId = tenantId || null;
  }

  /**
   * `tenant_id` de la MARCA a la que pertenece el usuario en sesión. Público
   * porque la pantalla de Marcas necesita saber si lo que acaba de editar es
   * *su* marca: solo entonces re-aplica el tema al instante.
   */
  userTenantId(): string | null {
    return this.sessionTenantId();
  }

  /**
   * Cambia la marca del usuario EN SESIÓN (el `tenant_id` que el login dejó en
   * sessionStorage) y la fija como resuelta.
   *
   * Hace falta porque la membresía se edita en caliente: si me quito de una
   * marca y me pongo en otra, el objeto de sesión seguiría diciendo la anterior
   * y la interfaz se repintaría con la marca vieja en el próximo reload.
   */
  setUserTenantId(tenantId: string | null): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const encrypted = this.browserStorage.getSessionStorageItem('user');
      if (encrypted) {
        const user = JSON.parse(this.encryption.decrypt(encrypted));
        if (Array.isArray(user)) {
          if (user[0]) user[0].tenant_id = tenantId;
        } else if (user) {
          user.tenant_id = tenantId;
        }
        this.browserStorage.setSessionStorageItem(
          'user', this.encryption.encrypt(JSON.stringify(user)),
        );
      }
    } catch {
      // Si el objeto de sesión no se puede reescribir, el override de abajo
      // igual deja la sesión actual con la marca correcta.
    }
    this.override(tenantId);
  }

  /** `tenant_id` del objeto user cifrado en sessionStorage (lo entrega el login). */
  private sessionTenantId(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      const encrypted = this.browserStorage.getSessionStorageItem('user');
      if (!encrypted) return null;
      const user = JSON.parse(this.encryption.decrypt(encrypted));
      const first = Array.isArray(user) ? user[0] : user;
      const id = first?.tenant_id;
      return typeof id === 'string' && id.trim() ? id.trim() : null;
    } catch {
      return null;
    }
  }

  /**
   * Override de QA: lee `?tenant=` de la URL (y lo persiste en la pestaña) o el
   * valor ya persistido. Solo slugs válidos: el valor termina en la ruta
   * `/api/v1/tenants/<id>/theme/`, así que nada fuera de `[a-z0-9-]` pasa.
   */
  private qaOverrideTenantId(): string | null {
    if (!environment.tenantThemeEnabled || !isPlatformBrowser(this.platformId)) return null;
    // ⚠️ FUERA de producción. En un build productivo cualquiera podía forzar la
    // marca de otro cliente en su navegador agregando `?tenant=` a la URL: es
    // solo cosmético, pero visible y confuso. La resolución productiva es por
    // DOMINIO (`/tenants/theme/by-host/`), así que acá el override ya no hace
    // falta; se conserva en dev/stage porque sigue siendo la única forma de
    // probar una marca ANTES de que exista su DNS y su certificado.
    if (!environment.tenantQaOverrideEnabled) return null;
    try {
      const raw = new URLSearchParams(this.document.location.search).get('tenant');
      const fromQs = raw ? raw.trim().toLowerCase() : '';
      if (fromQs && TENANT_SLUG_RE.test(fromQs)) {
        this.browserStorage.setSessionStorageItem(TENANT_QA_OVERRIDE_KEY, fromQs);
        return fromQs;
      }
      const stored = this.browserStorage.getSessionStorageItem(TENANT_QA_OVERRIDE_KEY);
      return stored && TENANT_SLUG_RE.test(stored) ? stored : null;
    } catch {
      return null;
    }
  }
}
